/**
 * URL Change Confirmation Modal
 *
 * Warns users about the implications of changing a link's destination URL.
 * Existing short URLs will redirect to the new destination immediately.
 *
 * @module components/modals/url-change-confirmation-modal
 */

import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Loader2, AlertTriangle, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { useUIStore } from '@/client/stores/ui-store';
import { apiFetch } from '@/client/lib/api';
import { useKeyboard } from '@/client/hooks/use-keyboard';
import { useIsMobile } from '@/client/hooks/use-mobile';

export function UrlChangeConfirmationModal() {
  const { urlChangeConfirmation, closeUrlChangeConfirmation } = useUIStore();
  const isMobile = useIsMobile();
  const [confirmText, setConfirmText] = useState('');
  const queryClient = useQueryClient();

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!urlChangeConfirmation) throw new Error('No URL change data');

      const response = await apiFetch(`/api/links/${urlChangeConfirmation.shortCode}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ destinationUrl: urlChangeConfirmation.newUrl }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update destination URL');
      }

      return response.json();
    },
    onSuccess: () => {
      toast.success('Destination URL updated successfully');
      queryClient.invalidateQueries({ queryKey: ['links'] });
      queryClient.invalidateQueries({ queryKey: ['links', urlChangeConfirmation?.shortCode] });
      closeUrlChangeConfirmation();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Reset confirm text when modal closes
  useEffect(() => {
    if (!urlChangeConfirmation) {
      setConfirmText('');
    }
  }, [urlChangeConfirmation]);

  // ⚡ Performance Optimization: Use shared keyboard hook instead of manual listener
  useKeyboard('Escape', closeUrlChangeConfirmation, !!urlChangeConfirmation);

  if (!urlChangeConfirmation) return null;

  const canConfirm = confirmText.toLowerCase() === 'change';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={closeUrlChangeConfirmation}
      />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-lg mx-4 bg-background rounded-lg shadow-xl border animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center gap-4 p-6 border-b">
          <div className="h-12 w-12 rounded-full bg-amber-500/10 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="h-6 w-6 text-amber-500" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Change Destination URL</h2>
            <p className="text-sm text-muted-foreground">This will affect all existing shares</p>
          </div>
          <button
            onClick={closeUrlChangeConfirmation}
            aria-label="Close URL change confirmation modal"
            className="ml-auto p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* URL Change Preview */}
          <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
            <div className="flex items-start gap-3">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide mt-1 w-14">
                Current
              </span>
              <p className="text-sm break-all flex-1 font-mono bg-muted/50 px-2 py-1 rounded">
                {urlChangeConfirmation.currentUrl}
              </p>
            </div>
            <div className="flex justify-center">
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="flex items-start gap-3">
              <span className="text-xs font-medium text-primary uppercase tracking-wide mt-1 w-14">
                New
              </span>
              <p className="text-sm break-all flex-1 text-primary font-mono bg-primary/5 px-2 py-1 rounded">
                {urlChangeConfirmation.newUrl}
              </p>
            </div>
          </div>

          {/* Warning */}
          <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-4">
            <p className="text-sm text-amber-700">
              <strong>Warning:</strong> Anyone who clicks the short link{' '}
              <code className="bg-muted px-1.5 py-0.5 rounded text-xs">
                arml.ink/{urlChangeConfirmation.shortCode}
              </code>{' '}
              will immediately be redirected to the new destination. This includes:
            </p>
            <ul className="text-sm text-amber-700 mt-2 ml-4 list-disc space-y-1">
              <li>Links shared on social media</li>
              <li>QR codes already printed</li>
              <li>Links in emails or documents</li>
            </ul>
          </div>

          {/* Confirmation Input */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Type <span className="font-mono bg-muted px-1.5 py-0.5 rounded">change</span> to
              confirm
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="w-full px-4 py-2 rounded-md border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Type 'change' to confirm"
              autoFocus={!isMobile}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t">
          <button
            onClick={closeUrlChangeConfirmation}
            className="px-4 py-2 rounded-md border hover:bg-muted transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => updateMutation.mutate()}
            disabled={!canConfirm || updateMutation.isPending}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground shadow hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {updateMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Updating…
              </>
            ) : (
              'Update Destination'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
